/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable import/no-cycle */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { H2, convertUTCStringToDate } from '@ouster/webviz-react-components';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components/macro';

import { ArchitectSceneTable } from '../components/ArchitectSceneTable';
import { useStoreState, useStoreActions } from '../reducers';
import { publicationUuidToURL } from '../reducers/architectScene';
import {
  LoadStatus,
  RawArchitectSceneInfo,
  ArchitectSceneInfo,
  PaginationInfo,
  User,
  OrderableField,
  ArchitectFilterState,
} from '../types';
import { BASE_ORG_API } from '../utils/constants';
import { useDeepCompareEffect } from '../utils/customHooks';
import { fetchWithAuth } from '../utils/requests';
import {
  orderArchitectFieldFromSearchParams,
  UrlArchitectSceneFilterState,
} from '../utils/searchParams';

// Data Categories that we do not want to show when the user loads the
// default homepage. We limit this feature to data categories for now
// because data categories are loading faster than other fields
// (/api/v1/data_categories returns faster than /api/v1/architect_scene_values)
// const CATEGORIES_TO_EXCLUDE_BY_DEFAULT = ['Integration Test'];

const ARCHITECT_SCENES_PER_PAGE = 200;
// TODO(pierre) on 2021-09-01: Move to requests.tsx

type ArchitectScenesGetResponse = {
  data: Array<RawArchitectSceneInfo>;
  current_page: number;
  total_count: number;
  total_pages: number;
  next_page?: string;
  previous_page?: string;
};

// Override semantic-ui grid weird spacing
// TODO(Pierre): Replace semantic-ui with CSS3 grid for more control over this page
const PageContainer = styled.div`
  display: grid;
  align-content: start;
  gap: 34px;
  overflow: hidden;
  height: 100%;
  position: relative;

  padding: 25px 36px 0px;
  div.ui.grid {
    margin: 0;
  }
  .ui.grid > .row {
    gap: 0.5em;
  }
  .ui.grid > .row > .column {
    padding: 0;
  }
`;

// TODO: We should make this generic so we do not have to add here things with every change
/**
 * Method transforms the JSON coming from the API into things that are consumable by JS.
 *
 * Mostly changing the snakecase to camelcase and transforming dates.
 * @param raw
 * @returns
 */
export const transformRawArchitectScenes: (
  raw: RawArchitectSceneInfo
) => ArchitectSceneInfo = (raw) => ({
  id: raw.id,
  createdAt: convertUTCStringToDate(raw.created_at),
  createdBy: raw.created_by,
  title: raw.title,
  publicTitle: raw.public_title,
  publicURL: raw.publication_uuid
    ? publicationUuidToURL(raw.publication_uuid)
    : undefined,
  tags: raw.tags,
});

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const ArchitectSceneList: React.FC<{ user?: User }> = ({ user }) => {
  const { activeFilters, orderByField } = useStoreState((storeState) => ({
    ...storeState.architectSceneList,
  }));
  const actions = useStoreActions((actions) => actions.architectSceneList);
  const { curUserOrg } = useStoreState((storeState) => storeState.user);

  const {
    updateFiltersBasedOnURL,
    loadFilterOptions,
    setDefaultOrderingInUrl,
    setOrderByField,
  } = actions;
  const { loadDataCategories } = useStoreActions(
    (actions) => actions.dataCategory
  );

  const isFirstRender = useRef(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /* 
    When user switch to organization that don't have architect
    we will to redirect user out of the page
  */
  useEffect(() => {
    if (curUserOrg && curUserOrg.id !== 1) {
      navigate('drive/private', { replace: true });
    }
  }, [curUserOrg, navigate]);

  // Whenever the query params in the URL change, update filters,
  // orderField, and data type in the reducer
  useEffect(() => {
    // Extract interesting fields from url search params
    const filtersInUrl =
      UrlArchitectSceneFilterState.fromSearchParams(searchParams);
    const orderInUrl = orderArchitectFieldFromSearchParams(searchParams);
    // When no order_by field is specified in the URL, update the URL to
    // the default ordering
    // If the field is there, update the reducer
    if (orderInUrl) {
      setOrderByField(orderInUrl);
    } else {
      setDefaultOrderingInUrl();
    }
    updateFiltersBasedOnURL(filtersInUrl);

    isFirstRender.current = false;
  }, [
    searchParams,
    // The deps below are reducer actions which won't change
    updateFiltersBasedOnURL,
    setOrderByField,
    setDefaultOrderingInUrl,
  ]);

  const [loadStatus, setLoadStatus] = useState<LoadStatus>('notStarted');
  const [architectScenes, setArchitectScenes] = useState<ArchitectSceneInfo[]>(
    []
  );
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    currentPage: 1,
    perPage: ARCHITECT_SCENES_PER_PAGE,
    totalCount: 0,
    totalPages: 1,
    nextPageUrl: null,
    previousPageUrl: null,
  });

  // TODO(pierre) on 2021-09-01: Move loadDrives into the reducer and
  // move the fetch into requests.tsx
  const loadArchitectScenes = useCallback(
    (url: string, fetchOpts?: RequestInit) => {
      setLoadStatus('loading');

      fetchWithAuth(url, fetchOpts)
        .then((resp) => {
          if (!resp.ok) {
            throw new Error('Bad Response');
          }
          return resp.json();
        })
        .then((result: ArchitectScenesGetResponse) => {
          const loadedArchitectScenes: ArchitectSceneInfo[] = [];

          for (const architectScene of result.data) {
            loadedArchitectScenes.push(
              transformRawArchitectScenes(architectScene)
            );
          }
          setPaginationInfo({
            currentPage: result.current_page,
            perPage: ARCHITECT_SCENES_PER_PAGE,
            totalCount: result.total_count,
            totalPages: result.total_pages,
            nextPageUrl: result.next_page ? result.next_page : null,
            previousPageUrl: result.previous_page ? result.previous_page : null,
          });
          setLoadStatus('success');
          setArchitectScenes(loadedArchitectScenes);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setLoadStatus('failed');
        });
    },
    []
  );

  const loadInitialPage = useCallback(
    (
      _filters: ArchitectFilterState,
      _orderByField: OrderableField,
      fetchOpts: RequestInit
    ) => {
      let filtersQueryParams = '';
      _filters.forEach((filterSection) => {
        if (!filterSection.values) {
          return;
        }

        let filterKey = filterSection.sectionInfo.filterField as string;
        // Rename 'tags' key to 'tag' when filtering to match the API
        filterKey = filterKey === 'tags' ? 'tag' : filterKey;
        filterSection.values.forEach((data) => {
          filtersQueryParams = filtersQueryParams.concat(
            `&filter=${filterKey}=${data.name}`
          );
        });
      });
      if (curUserOrg?.id) {
        loadArchitectScenes(
          `${BASE_ORG_API}/${curUserOrg.id}/architect_scenes/?page=1&per_page=${ARCHITECT_SCENES_PER_PAGE}&order_by=${_orderByField}${filtersQueryParams}`,
          fetchOpts
        );
      }
    },
    [loadArchitectScenes, curUserOrg?.id]
  );

  const loadNextPage = useCallback(
    (_paginationInfo: PaginationInfo) => {
      if (_paginationInfo.nextPageUrl) {
        loadArchitectScenes(_paginationInfo.nextPageUrl);
      } else {
        console.error('Next page was not returned in previous API response');
      }
    },
    [loadArchitectScenes]
  );

  const loadPreviousPage = useCallback(
    (_paginationInfo: PaginationInfo) => {
      if (_paginationInfo.previousPageUrl) {
        loadArchitectScenes(_paginationInfo.previousPageUrl);
      } else {
        console.error(
          'Previous page was not returned in previous API response'
        );
      }
    },
    [loadArchitectScenes]
  );

  // Reload list of architect_scenes when dataType or filters change
  useDeepCompareEffect(() => {
    // Abort signal kept as a closure here so that we can cleanup the
    // request if a new fetch is launched
    const abortController = new AbortController();
    const abortSignal: RequestInit = { signal: abortController.signal };

    // Only load the first page after filtering options have been set from the URL
    if (orderByField) {
      loadInitialPage(activeFilters || [], orderByField, abortSignal);
    }

    // Abort the promise when a new page load is launched upon this
    // useEffect cleanup
    return () => {
      abortController.abort();
    };
  }, [activeFilters, orderByField, loadInitialPage]);
  // Load filter options for dropdown lists on first render
  useEffect(() => {
    if (curUserOrg?.id) loadDataCategories(curUserOrg.id);
  }, [loadDataCategories, curUserOrg?.id]);
  useEffect(() => {
    if (curUserOrg?.id) loadFilterOptions(curUserOrg.id);
  }, [loadFilterOptions, curUserOrg?.id]);
  return (
    <PageContainer>
      <H2 fontWeight="medium" fontSize={30}>
        Architect Scene
      </H2>
      <ArchitectSceneTable
        architect_scenes={architectScenes}
        paginationInfo={paginationInfo}
        onPageChange={(nextPage: boolean) => {
          nextPage
            ? loadNextPage(paginationInfo)
            : loadPreviousPage(paginationInfo);
        }}
        loadStatus={loadStatus}
      />
    </PageContainer>
  );
};

export default ArchitectSceneList;
